import { useState, useEffect } from 'react';
import bookingsAPI from '../services/bookings.api';

// Format a Date as YYYY-MM-DD using its LOCAL calendar day.
// toISOString() converts to UTC first, which shifts the day back for timezones
// ahead of UTC (IST = UTC+5:30) and drops the last day of every month.
const toLocalYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface UseBlockedDatesResult {
  blockedDates: Set<string>;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Custom hook to fetch and manage blocked dates for a venue
 * @param venueId - The venue ID to fetch blocked dates for
 * @param month - The month to fetch blocked dates for
 * @param enabled - Whether to fetch data (default: true)
 */
export const useBlockedDates = (
  venueId: string | null | undefined,
  month: Date,
  enabled: boolean = true,
    venueName?: string           // ✅ ADD THIS

): UseBlockedDatesResult => {
  const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBlockedDates = async () => {
    if (!venueId || !enabled) {
      setBlockedDates(new Set());
      return;
    }

     // ✅ Skip only Sanjeevaiah Park by name
    if (venueName?.toLowerCase().includes('sanjeevaiah')) {
      setBlockedDates(new Set());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
      const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);

      const startDateStr = toLocalYMD(startDate);
      const endDateStr = toLocalYMD(endDate);

      console.log('useBlockedDates: Fetching...', {
        venueId,
        startDateStr,
        endDateStr,
      });

      const dates = await bookingsAPI.getBlockedDates(
        venueId,
        startDateStr,
        endDateStr
      );

      console.log('useBlockedDates: Received', dates.length, 'blocked dates');
      setBlockedDates(new Set(dates));
    } catch (err) {
      console.error('useBlockedDates: Error', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch blocked dates'));
      setBlockedDates(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedDates();
  }, [venueId, month.getFullYear(), month.getMonth(), enabled]);

  return {
    blockedDates,
    loading,
    error,
    refetch: fetchBlockedDates,
  };
};

export default useBlockedDates;